/**
 * The public view of a focused v4 snapshot: the PA = NO accounts, or the
 * write-downs, exactly as the tab showed them when they were shared — the same
 * columns, the same summary figures, the same month-by-month detail. Nothing
 * from the general position is added; the recipient sees what the sender saw.
 */
import React, { useMemo, useState } from 'react';
import { ChevronDown, ChevronRight, Search } from 'lucide-react';
import type { SharedV3Payload, SharedV3Row } from '../../lib/raketechShareV3';

const fmt = (v: number) =>
  new Intl.NumberFormat('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(v || 0);
const fmtInt = (v: number) => new Intl.NumberFormat('en-GB').format(v || 0);

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const fmtMonth = (m: string) => {
  const [y, mo] = (m || '').split('-');
  return MONTHS[parseInt(mo, 10) - 1] ? `${MONTHS[parseInt(mo, 10) - 1]} ${y}` : m;
};

type MonthCol = 'ftds' | 'invoiced' | 'june' | 'reported' | 'restated' | 'earned';
const MONTH_NAMES: Record<MonthCol, string> = {
  ftds: 'FTDs', invoiced: 'Invoiced (their monthly report)', june: 'June 2025 file',
  reported: 'Reported at the time', restated: 'July 2026 report', earned: 'Relied on',
};

interface Props {
  payload: SharedV3Payload & { focus: NonNullable<SharedV3Payload['focus']> };
  title: string;
  description: string | null;
  createdAt: string;
  dark: boolean;
}

const SharedFocusView: React.FC<Props> = ({ payload, title, description, createdAt, dark }) => {
  const [search, setSearch] = useState('');
  const [open, setOpen] = useState<Set<string>>(new Set());

  const card = dark ? 'bg-[hsl(225,15%,10%)] border-[hsl(225,15%,18%)]' : 'bg-white border-gray-200';
  const head = dark ? 'bg-[hsl(225,15%,8%)]' : 'bg-gray-100';
  const primary = dark ? 'text-white' : 'text-gray-900';
  const secondary = dark ? 'text-zinc-400' : 'text-gray-500';
  const muted = dark ? 'text-zinc-500' : 'text-gray-400';
  const value = dark ? 'text-zinc-300' : 'text-gray-700';
  const faint = dark ? 'text-zinc-600' : 'text-gray-300';
  const divider = dark ? 'divide-[hsl(225,15%,13%)]' : 'divide-gray-100';
  const hover = dark ? 'hover:bg-[hsl(225,15%,12%)]' : 'hover:bg-gray-50';
  const input = dark
    ? 'bg-[hsl(225,15%,12%)] border-[hsl(225,15%,20%)] text-white placeholder-zinc-500'
    : 'bg-white border-gray-300 text-gray-900 placeholder-gray-400';
  const drill = dark ? 'bg-[hsl(225,15%,8%)] border-[hsl(225,15%,15%)]' : 'bg-gray-50 border-gray-200';

  const TONE: Record<string, string> = {
    amber: 'border-amber-500/30 bg-amber-500/10 text-amber-400',
    red: 'border-red-500/30 bg-red-500/10 text-red-400',
    emerald: 'border-emerald-500/30 bg-emerald-500/10 text-emerald-400',
    zinc: dark ? 'border-zinc-600/40 bg-zinc-600/10 text-zinc-400' : 'border-gray-300 bg-gray-100 text-gray-500',
  };
  const Tag: React.FC<{ tone: keyof typeof TONE; title?: string; children: React.ReactNode }> = ({ tone, title: t, children }) => (
    <span title={t} className={`px-1.5 py-0.5 rounded text-[10px] border whitespace-nowrap ${TONE[tone]}`}>{children}</span>
  );
  const Stat: React.FC<{ label: string; value: string; hint?: string; tone?: string }> = ({ label, value: v, hint, tone }) => (
    <div className={`rounded-xl border p-4 ${card}`}>
      <div className={`text-[11px] uppercase tracking-wide mb-1 ${muted}`}>{label}</div>
      <div className={`text-lg font-semibold ${tone || primary}`}>{v}</div>
      {hint && <div className={`text-[11px] mt-1 ${secondary}`}>{hint}</div>}
    </div>
  );
  const Th: React.FC<{ children?: React.ReactNode; right?: boolean }> = ({ children, right }) => (
    <th className={`px-3 py-2.5 font-medium ${right ? 'text-right' : 'text-left'}`}>{children}</th>
  );

  const kind = payload.focus.kind;
  const rows = useMemo(() => {
    const q = search.trim().toLowerCase();
    return payload.rows.filter((r) => !q || [r.accountId, r.partner, ...r.brands].some((f) => String(f || '').toLowerCase().includes(q)));
  }, [payload.rows, search]);

  const toggle = (key: string) => setOpen((prev) => {
    const next = new Set(prev);
    if (next.has(key)) next.delete(key); else next.add(key);
    return next;
  });

  const f = (r: SharedV3Row) => r.facts!;

  const MonthTable: React.FC<{ row: SharedV3Row; columns: MonthCol[] }> = ({ row, columns }) => (
    <table className="w-full max-w-3xl text-[11px]">
      <thead>
        <tr className={muted}>
          <th className="text-left py-0.5 pr-3 font-medium">Month</th>
          {columns.map((c) => <th key={c} className="text-right py-0.5 px-3 font-medium">{MONTH_NAMES[c]}</th>)}
        </tr>
      </thead>
      <tbody className={`divide-y ${divider}`}>
        {row.months.map(([month, invoiced, ftds, earned, june, reported, restated]) => {
          const pick: Record<MonthCol, number> = { ftds, invoiced, june, reported, restated, earned };
          return (
            <tr key={month} className={secondary}>
              <td className="py-1 pr-3">{fmtMonth(month)}</td>
              {columns.map((c) => {
                const v = pick[c];
                return <td key={c} className={`py-1 px-3 text-right tabular-nums ${c === 'ftds' ? '' : v ? value : faint}`}>{c === 'ftds' ? (v || '—') : v ? fmt(v) : '—'}</td>;
              })}
            </tr>
          );
        })}
      </tbody>
    </table>
  );

  // ---- PA = NO -------------------------------------------------------------
  const paTotals = useMemo(() => ({
    theirs: rows.reduce((s, r) => s + f(r).theirFigure, 0),
    invoiced: rows.reduce((s, r) => s + r.invoiced, 0),
    ftds: rows.reduce((s, r) => s + f(r).ftds, 0),
    exact: rows.filter((r) => f(r).exactMatch).length,
    noLogin: rows.filter((r) => f(r).noLogin).length,
  }), [rows]);

  // ---- Write-downs -----------------------------------------------------------
  const wdTotals = useMemo(() => ({
    writeDown: rows.reduce((s, r) => s + f(r).writeDown.writeDown, 0),
    reportedToJune: rows.reduce((s, r) => s + f(r).writeDown.reportedToJune, 0),
    juneToJuly: rows.reduce((s, r) => s + f(r).writeDown.juneToJuly, 0),
    tail: rows.reduce((s, r) => s + f(r).writeDown.tail, 0),
    nulled: rows.filter((r) => f(r).writeDown.nulled).length,
    commented: rows.filter((r) => r.comments.length > 0).length,
  }), [rows]);

  return (
    <div className="max-w-[1600px] mx-auto">
      <div className="mb-6">
        <h1 className={`text-xl font-semibold ${primary}`}>{title}</h1>
        <p className={`text-xs mt-1 ${muted}`}>
          {fmtMonth(payload.period.from)} to {fmtMonth(payload.period.to)} · {payload.totals.accounts} accounts ·
          prepared {new Date(createdAt).toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' })}
        </p>
        {description && <p className={`text-sm mt-3 max-w-4xl leading-relaxed ${value}`}>{description}</p>}
      </div>

      <div className={`rounded-xl border p-4 mb-5 text-xs leading-relaxed ${card} ${value}`}>
        <p className="mb-2">{payload.focus.note}</p>
        <p className={muted}>
          Selection: {payload.focus.selection}. Figures are Raketech's own, from its June 2025 reconciliation, its July
          2026 report and its monthly Commission reports, set beside what Adwin invoiced on those reports. Open an
          account for the month-by-month detail.
        </p>
      </div>

      {kind === 'pa_no_players' ? (
        <div className="grid grid-cols-2 lg:grid-cols-5 gap-3 mb-5">
          <Stat label="Accounts" value={fmtInt(rows.length)} hint="Classified PA = NO by Raketech" />
          <Stat label="On Raketech's figures" value={`EUR ${fmt(paTotals.theirs)}`} hint={`EUR ${fmt(paTotals.invoiced)} invoiced by Adwin`} />
          <Stat label="Acquisitions" value={fmtInt(paTotals.ftds)} hint="On Raketech's monthly reports" />
          <Stat label="Match to the cent" value={`${paTotals.exact} of ${rows.length}`} hint="Raketech's figure equals Adwin's invoice" tone="text-amber-400" />
          <Stat label="No Adwin login" value={fmtInt(paTotals.noLogin)} hint="Of the accounts shown" tone="text-red-400" />
        </div>
      ) : (
        <div className="grid grid-cols-2 lg:grid-cols-6 gap-3 mb-5">
          <Stat label="Accounts" value={fmtInt(rows.length)} hint="Reported, invoiced on, later reduced" />
          <Stat label="Total reduction" value={`EUR ${fmt(wdTotals.writeDown)}`} hint="Across the three steps" tone="text-red-400" />
          <Stat label="Reported → June credited" value={`EUR ${fmt(wdTotals.reportedToJune)}`} hint="Inside their June 2025 file" tone="text-amber-400" />
          <Stat label="June → July report" value={`EUR ${fmt(wdTotals.juneToJuly)}`} hint="Between their two files" tone="text-amber-400" />
          <Stat label="Monthly reports → July, Dec 24 on" value={`EUR ${fmt(wdTotals.tail)}`} hint="Later months" tone="text-amber-400" />
          <Stat label="Reduced to nil" value={fmtInt(wdTotals.nulled)} hint={`${wdTotals.commented} carry a Raketech comment`} />
        </div>
      )}

      <div className="flex flex-wrap items-center gap-2 mb-3">
        <div className="relative flex-1 min-w-[240px] max-w-md">
          <Search className={`absolute left-3 top-1/2 -translate-y-1/2 h-3.5 w-3.5 ${muted}`} />
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search account id, partner or brand"
            className={`w-full pl-9 pr-3 py-2 text-xs rounded-lg border outline-none ${input}`}
          />
        </div>
      </div>

      <div className={`rounded-xl border overflow-hidden ${card}`}>
        <div className="overflow-x-auto">
          {kind === 'pa_no_players' ? (
            <table className="w-full text-xs">
              <thead className={head}>
                <tr className={muted}>
                  <th className="w-8" />
                  <Th>Account</Th>
                  <Th>Partner / brand</Th>
                  <Th>Created</Th>
                  <Th>First FTD</Th>
                  <Th>Last FTD</Th>
                  <Th right>FTDs</Th>
                  <Th right>Raketech's figure</Th>
                  <Th right>Invoiced</Th>
                  <Th right>Difference</Th>
                  <Th>Flags</Th>
                </tr>
              </thead>
              <tbody className={`divide-y ${divider}`}>
                {rows.map((row) => {
                  const x = f(row);
                  const diff = Math.round((x.theirFigure - row.invoiced) * 100) / 100;
                  return (
                    <React.Fragment key={row.key}>
                      <tr className={`${hover} cursor-pointer align-top`} onClick={() => toggle(row.key)}>
                        <td className="pl-3 pt-3">{open.has(row.key) ? <ChevronDown className={`h-3.5 w-3.5 ${muted}`} /> : <ChevronRight className={`h-3.5 w-3.5 ${muted}`} />}</td>
                        <td className={`px-3 py-2.5 font-mono ${value}`}>
                          {row.accountId}
                          {x.pa === 'mixed' && <div className="font-sans text-[10px] text-amber-400/80">mixed PA</div>}
                          {x.status && <div className={`font-sans text-[10px] ${faint}`}>{x.status}</div>}
                        </td>
                        <td className="px-3 py-2.5"><div className={value}>{row.brands.slice(0, 2).join(' / ') || '—'}</div><div className={`text-[10px] ${muted}`}>{row.partner}</div></td>
                        <td className={`px-3 py-2.5 whitespace-nowrap ${secondary}`}>{x.created ? x.created.slice(0, 10) : '—'}</td>
                        <td className={`px-3 py-2.5 whitespace-nowrap ${value}`}>{x.firstFtd ? fmtMonth(x.firstFtd) : '—'}</td>
                        <td className={`px-3 py-2.5 whitespace-nowrap ${value}`}>{x.lastFtd ? fmtMonth(x.lastFtd) : '—'}</td>
                        <td className={`px-3 py-2.5 text-right tabular-nums ${value}`}>{x.ftds ? fmtInt(x.ftds) : '—'}<div className={`text-[10px] ${faint}`}>{x.monthsWithFtds ? `${x.monthsWithFtds} mo` : ''}</div></td>
                        <td className={`px-3 py-2.5 text-right tabular-nums ${value}`}>{fmt(x.theirFigure)}</td>
                        <td className={`px-3 py-2.5 text-right tabular-nums ${value}`}>{fmt(row.invoiced)}</td>
                        <td className={`px-3 py-2.5 text-right tabular-nums ${diff > 1 ? 'text-red-400' : diff < -1 ? 'text-amber-400' : faint}`}>{fmt(diff)}</td>
                        <td className="px-3 py-2.5">
                          <div className="flex flex-wrap gap-1">
                            {x.exactMatch && <Tag tone="amber" title="Raketech's figure equals Adwin's invoicing to the cent">to the cent</Tag>}
                            {x.noLogin && <Tag tone="red" title="Adwin has no login to the operator account">no login</Tag>}
                            {x.shared && <Tag tone="amber" title="Account shared with other publishers">shared</Tag>}
                            {row.comments.length > 0 && <Tag tone="zinc" title={row.comments.join(' | ')}>comment</Tag>}
                          </div>
                        </td>
                      </tr>
                      {open.has(row.key) && (
                        <tr>
                          <td colSpan={11} className={`px-4 py-3 border-y ${drill}`}>
                            <div className={`text-[10px] mb-2 ${muted}`}>Deal codes: {x.dealCodes.join(', ') || '—'}</div>
                            {row.comments.length > 0 && (
                              <div className={`text-[11px] italic mb-2 ${value}`}>Raketech's own comment: {row.comments.map((c) => `"${c}"`).join(' ')}</div>
                            )}
                            <MonthTable row={row} columns={['ftds', 'invoiced', 'june', 'restated', 'earned']} />
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })}
                {rows.length === 0 && <tr><td colSpan={11} className={`px-3 py-6 text-center ${muted}`}>No accounts match the search.</td></tr>}
              </tbody>
              <tfoot className={head}>
                <tr className={value}>
                  <td />
                  <td className="px-3 py-2.5 font-medium" colSpan={5}>{rows.length} accounts</td>
                  <td className="px-3 py-2.5 text-right tabular-nums font-medium">{fmtInt(paTotals.ftds)}</td>
                  <td className="px-3 py-2.5 text-right tabular-nums font-medium">{fmt(paTotals.theirs)}</td>
                  <td className="px-3 py-2.5 text-right tabular-nums font-medium">{fmt(paTotals.invoiced)}</td>
                  <td className="px-3 py-2.5 text-right tabular-nums font-medium">{fmt(paTotals.theirs - paTotals.invoiced)}</td>
                  <td />
                </tr>
              </tfoot>
            </table>
          ) : (
            <table className="w-full text-xs">
              <thead className={head}>
                <tr className={muted}>
                  <th className="w-8" />
                  <Th>Account</Th>
                  <Th>Partner / brand</Th>
                  <Th right>Invoiced Mar 23 – Nov 24</Th>
                  <Th right>Reported at the time</Th>
                  <Th right>June 2025 credited</Th>
                  <Th right>July 2026 report</Th>
                  <Th right>Dec 24 on: invoiced / July</Th>
                  <Th right>Reduction</Th>
                  <Th right>%</Th>
                  <Th right>Difference, whole period</Th>
                  <Th>Raketech's own words</Th>
                </tr>
              </thead>
              <tbody className={`divide-y ${divider}`}>
                {rows.map((row) => {
                  const x = f(row); const w = x.writeDown;
                  const inJune = row.candidates.some((c) => c.source === 'june_recon') || w.reported > 0;
                  const hasJuly = x.dealCodes.length > 0;
                  return (
                    <React.Fragment key={row.key}>
                      <tr className={`${hover} cursor-pointer align-top`} onClick={() => toggle(row.key)}>
                        <td className="pl-3 pt-3">{open.has(row.key) ? <ChevronDown className={`h-3.5 w-3.5 ${muted}`} /> : <ChevronRight className={`h-3.5 w-3.5 ${muted}`} />}</td>
                        <td className={`px-3 py-2.5 font-mono ${value}`}>{row.accountId}<div className={`font-sans text-[10px] ${faint}`}>PA {x.pa}</div></td>
                        <td className="px-3 py-2.5"><div className={value}>{row.brands.slice(0, 2).join(' / ') || '—'}</div><div className={`text-[10px] ${muted}`}>{row.partner}</div></td>
                        <td className={`px-3 py-2.5 text-right tabular-nums ${value}`}>{x.invoicedRecon ? fmt(x.invoicedRecon) : '—'}</td>
                        <td className={`px-3 py-2.5 text-right tabular-nums ${value}`}>{w.reported ? fmt(w.reported) : '—'}</td>
                        <td className={`px-3 py-2.5 text-right tabular-nums ${w.reportedToJune > 1 ? 'text-red-400' : secondary}`}>{inJune ? fmt(w.june) : '—'}</td>
                        <td className={`px-3 py-2.5 text-right tabular-nums ${w.juneToJuly > 1 ? 'text-red-400' : secondary}`}>{hasJuly ? fmt(w.july) : '—'}</td>
                        <td className={`px-3 py-2.5 text-right tabular-nums whitespace-nowrap ${secondary}`}>
                          {x.invoicedPostRecon || x.earnedTail ? <>{fmt(x.invoicedPostRecon)} / <span className={w.tail > 0 ? 'text-red-400' : ''}>{fmt(x.earnedTail)}</span></> : '—'}
                        </td>
                        <td className="px-3 py-2.5 text-right tabular-nums text-red-400">
                          {fmt(w.writeDown)}
                          <div className={`text-[10px] ${faint}`}>{[w.reportedToJune > 1 ? `R→J ${fmt(w.reportedToJune)}` : '', w.juneToJuly > 1 ? `J→Jul ${fmt(w.juneToJuly)}` : '', w.tail > 0 ? `Dec 24 on ${fmt(w.tail)}` : ''].filter(Boolean).join(' · ')}</div>
                        </td>
                        <td className={`px-3 py-2.5 text-right tabular-nums ${secondary}`}>{w.pct ? `${Math.round(w.pct * 100)}%` : '—'}</td>
                        <td className={`px-3 py-2.5 text-right tabular-nums ${row.difference > 1 ? 'text-red-400' : row.difference < -1 ? 'text-amber-400' : faint}`} title="Raketech's figure less Adwin's invoicing, all months">{fmt(row.difference)}</td>
                        <td className={`px-3 py-2.5 text-[10px] max-w-[260px] ${muted}`}>
                          <div className="flex flex-wrap gap-1 mb-1">
                            {w.nulled && <Tag tone="red">nulled</Tag>}
                            {w.ownData ? <Tag tone="emerald" title="Adwin holds an operator-backend or manual figure">own data</Tag> : <Tag tone="zinc" title="Only Raketech's figures exist for this account">no own data</Tag>}
                            {x.noLogin && <Tag tone="red" title="Adwin has no login to the operator account">no login</Tag>}
                          </div>
                          {row.comments.join(' · ') || '—'}
                        </td>
                      </tr>
                      {open.has(row.key) && (
                        <tr>
                          <td colSpan={12} className={`px-4 py-3 border-y ${drill}`}>
                            <MonthTable row={row} columns={['invoiced', 'reported', 'june', 'restated']} />
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })}
                {rows.length === 0 && <tr><td colSpan={12} className={`px-3 py-6 text-center ${muted}`}>No accounts match the search.</td></tr>}
              </tbody>
              <tfoot className={head}>
                <tr className={value}>
                  <td />
                  <td className="px-3 py-2.5 font-medium" colSpan={2}>{rows.length} accounts</td>
                  <td className="px-3 py-2.5 text-right tabular-nums font-medium">{fmt(rows.reduce((s, r) => s + f(r).invoicedRecon, 0))}</td>
                  <td className="px-3 py-2.5 text-right tabular-nums font-medium">{fmt(rows.reduce((s, r) => s + f(r).writeDown.reported, 0))}</td>
                  <td className="px-3 py-2.5 text-right tabular-nums font-medium">{fmt(rows.reduce((s, r) => s + f(r).writeDown.june, 0))}</td>
                  <td className="px-3 py-2.5 text-right tabular-nums font-medium">{fmt(rows.reduce((s, r) => s + f(r).writeDown.july, 0))}</td>
                  <td className="px-3 py-2.5 text-right tabular-nums font-medium whitespace-nowrap">{fmt(rows.reduce((s, r) => s + f(r).invoicedPostRecon, 0))} / {fmt(rows.reduce((s, r) => s + f(r).earnedTail, 0))}</td>
                  <td className="px-3 py-2.5 text-right tabular-nums font-medium text-red-400">{fmt(wdTotals.writeDown)}</td>
                  <td />
                  <td className="px-3 py-2.5 text-right tabular-nums font-medium">{fmt(rows.reduce((s, r) => s + r.difference, 0))}</td>
                  <td />
                </tr>
              </tfoot>
            </table>
          )}
        </div>
      </div>

      <p className={`text-[11px] mt-4 ${muted}`}>
        All amounts in EUR. {kind === 'pa_no_players'
          ? 'Raketech\'s figure is its July 2026 report where it carries the account, otherwise its June 2025 file for the reconciled period, plus its monthly reports from December 2024. A positive difference means Raketech\'s figure exceeds what Adwin invoiced.'
          : 'Each reduction is measured on its own pair of Raketech columns — Reported against June 2025 credited, June 2025 against the July 2026 report, and from December 2024 the monthly reports against the July 2026 report — so nothing is counted twice. The whole-period difference is Raketech\'s current figure less Adwin\'s invoicing, all months.'}
      </p>
    </div>
  );
};

export default SharedFocusView;
